import { memo, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, CheckboxField, Badge as AmplifyBadge } from '@aws-amplify/ui-react'
import {
  DgtPdOrderOperation, 
  DgtPdProductionOrder, 
} from "../models";
import Modal from "../components/Modal";
import Stepper from "./Stepper";
import { formatNumberV2 } from "../utilities/Format";
import FGInspectMenu from "./FGInspectMenu";
import SpinnerLoading from "./SpinnerLoading";
import DoComplete from "./DoComplete";
import UserContext from "../contexts/UserContext";
import { OrderInspectionLog } from "./types";
import Badge from "./Badge";
import { 
  createInspectionLog,
  generateFGInspectionForm,
  getAverageFillingWeightApi,
  submitInspectionLog,
  reworkInspectionLog
} from "../apis";
import { FGInspectionTypes } from "../constants/EnumType";
import { TbReload, TbCopy } from "react-icons/tb";
import PicklistContext from "../contexts/PicklistContext";
import { convertJson } from "../utilities/Validate";
import { getBulkItems } from "../states/PicklistAction";

export enum UIState {
  Init,
  Loading,
  Success
}

const LabelwithIcon = (
  title: string, value: string
) => <div className="flex flex-col">
    <small>{title}</small>
    <label className={`text-xl font-bold`}>{value}</label>
  </div>

export const FGInspection = ({ 
  show, onHide, productionOrder, operation, fgInspectionLogs, mutate
} : { 
  show: boolean, 
  onHide: () => void, 
  productionOrder : DgtPdProductionOrder,
  operation: DgtPdOrderOperation,
  fgInspectionLogs: OrderInspectionLog[],
  mutate: (updatedLog: OrderInspectionLog) => void,
  onCompletedEvent?: () => void
}) => {

  const { user, readOnly } = useContext(UserContext);
  const currentUser: any = user;

  const displayUsername = currentUser?.name || currentUser?.givenName || currentUser?.username || '';

  const [ currentStep, setCurrentStep ] = useState<number>(1);
  const [ selectedForm, setSelectedForm ] = useState<OrderInspectionLog>();
  const [ confirmChecked, setConfirmChecked ] = useState<boolean>(false);
  const [ complete, setComplete ] = useState<boolean>(false);
  const [ uiState, setUIState ] = useState<UIState>(UIState.Init);
  const [ loading, setLoading ] = useState<boolean>(false);
  const [ isCountingState, setIsCountingState ] = useState<boolean>(false);
  const [copiedBatch, setCopiedBatch] = useState<string | null>(null);

  const picklistContext = useContext(PicklistContext)

  const onNext = useCallback(() => {
    setCurrentStep(prev => prev + 1);
  }, []);

  const disabledNext = useMemo(() => {
    if (currentStep === 1) return false;

    const notStartOrSubmittedYet = selectedForm?.id === '' || selectedForm?.inspectionStatus === 'SUBMITTED';

    if (currentStep === 2 && selectedForm && notStartOrSubmittedYet) return true;
    if (currentStep === 3 && selectedForm && !confirmChecked) return true;

    return false;
  }, [ currentStep, selectedForm, confirmChecked ])

  const onLinkToExcelSharePoint = useCallback(async () => {
    if (selectedForm && selectedForm.formUrl) {
      window.open(selectedForm.formUrl, '_blank');
      return;
    }

    setLoading(true);
    try {
      const bulkComponents = picklistContext ? picklistContext.items
        .filter(item => item.materialType === 'BULK') : await getBulkItems(productionOrder.id)
      let bulkCode = undefined;
      let bulkDescription = undefined;
      if (bulkComponents.length > 0) {
        bulkCode = bulkComponents[0].materialCode;
        bulkDescription = bulkComponents[0].materialDescription;
      }
      const generatedForm = await generateFGInspectionForm(
        productionOrder.id,
        (selectedForm?.inspectionType || '').toLowerCase(),
        productionOrder.materialCode,
        productionOrder.materialDescription,
        productionOrder.infoRefPkg!,
        operation.actualWorkCenterId ? operation.actualWorkCenterId : operation.workCenterId,
        operation.actualWorkCenterDescription ? operation.actualWorkCenterDescription : operation.workCenterDescription!,
        productionOrder.infoFwt!,
        new Date(),
        bulkCode || '',
        bulkDescription || '',
      )
      const insertLog = await createInspectionLog(
        productionOrder.id,
        operation.actualWorkCenterId ? operation.actualWorkCenterId : operation.workCenterId,
        operation.actualWorkCenterDescription ? operation.actualWorkCenterDescription : operation.workCenterDescription!,
        selectedForm?.inspectionType as FGInspectionTypes,
        generatedForm.name!,
        generatedForm.workUrl!,
        displayUsername
      )

      const mutateData = { ...insertLog, canEdit: true }
      setSelectedForm(mutateData);
      mutate(mutateData);
      
      // Note - No auto open link, please user click again
    } catch (error : any) {
      alert(`เกิดข้อผิดพลาดในการสร้างแบบฟอร์ม กรุณาลองใหม่อีกครั้ง (${error.message})`)
    }
    setLoading(false);
  }, [ productionOrder, operation, selectedForm, displayUsername, mutate, picklistContext ]);

  const onCompleted = useCallback(async () => {
    setUIState(UIState.Loading);

    try {
      let avgFwtWeightRes = undefined;
      if (selectedForm?.inspectionType === FGInspectionTypes.PD_FILLING) {
        avgFwtWeightRes = await getAverageFillingWeightApi(
          productionOrder.id,
          operation.actualWorkCenterId ? operation.actualWorkCenterId : operation.workCenterId,
        )
      }

      const updateLog = await submitInspectionLog(
        selectedForm?.id!,
        selectedForm?._version || 1,
        displayUsername,
        avgFwtWeightRes
      )

      const mutateData = { ...updateLog, canEdit: true }
      setSelectedForm(mutateData);
      mutate(mutateData);

      setComplete(true);
    } catch (error : any) {
      alert(`เกิดข้อผิดพลาดในการส่งงาน กรุณาลองใหม่อีกครั้ง (${error.message})`)
    }

    setUIState(UIState.Success);
  }, [ operation, productionOrder, selectedForm, mutate, displayUsername ]);

  const onRework = useCallback(async () => {
    const reworkForm = await reworkInspectionLog(
      selectedForm?.id!,
      selectedForm?._version || 1,
      displayUsername
    )

    const mutateData = { ...reworkForm, canEdit: true }
    setSelectedForm(mutateData);
    mutate(mutateData);

    setComplete(false);
  }, [ selectedForm, displayUsername, mutate ]);

  const onClosed = useCallback(() => {
    onHide();
    setCurrentStep(1)
    setSelectedForm(undefined);
    setConfirmChecked(false);
    setComplete(false);
    setUIState(UIState.Init);
  }, [ onHide ]);

  const batchNumbers = useMemo(() => {
    let batchNumbers: string[] = []
      if (productionOrder.actualBatchNumbers) {
        const actualBatchNumbers: string[] = convertJson(productionOrder.actualBatchNumbers)

        const originalBatch: string[] = convertJson(productionOrder.batchNumbers)

        actualBatchNumbers.forEach(batch => {
          const sixLastDigit = batch.slice(-6)
          const matchSuffix = originalBatch.find(b => b.slice(-6) === sixLastDigit)
          if (matchSuffix) batchNumbers.push(batch)
        })

        originalBatch.forEach(origin => {
          if (!batchNumbers.map(b => b.slice(-6)).includes(origin.slice(-6))) batchNumbers.push(origin)
        })

        batchNumbers = batchNumbers.filter((item, index) => batchNumbers.indexOf(item) === index);
      } else if (productionOrder.batchNumbers) {
        batchNumbers = convertJson(productionOrder.batchNumbers)
      }
      return batchNumbers
  }, [ productionOrder ]);

  useEffect(() => {
    fgInspectionLogs.forEach(log => log.id !== '' && setIsCountingState(true));
  }, [ fgInspectionLogs ])

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedBatch(text);
      setTimeout(() => setCopiedBatch(null), 2000); // Hide tooltip after 2 seconds
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  return (
    <Modal
      show={show}
      onHide={onClosed}
      title={`ลงบันทึกคุณภาพสินค้าสำเร็จรูป - ${productionOrder.id}`}
      maxWidth='max-w-4xl'
      disableClickOutsite={true}
      showCloseButton={true}
      >
      <>
        <div className='grid grid-cols-2 gap-2 mb-5'>
          <div className={`flex flex-col ${!isCountingState ? 'animate-bounce' : '' }`}>
            <small>เครื่องจักร</small>
            <label className={`text-xl font-bold ${!isCountingState ? 'animate-pulse text-yellow-500' : ''}`}>{operation.actualWorkCenterDescription ? operation.actualWorkCenterDescription : operation.workCenterDescription} ({operation.actualWorkCenterId ? operation.actualWorkCenterId : operation.workCenterId})</label>
          </div>
          {LabelwithIcon('สินค้า', `${productionOrder.materialDescription} (${productionOrder.materialCode})`)}
          {LabelwithIcon('จำนวน', `${formatNumberV2(productionOrder.orderQty)} ${productionOrder.materialUnit}`)}
          <div>
            <small>ชนิด ORDER</small>
            <h6 className='basis-3/4 sm:basis-2/4 lg:basis-2/3'>
              {productionOrder.orderType === 'REPACK' && <Badge variant="order-repack"><>{productionOrder.orderType}</></Badge>}
              {productionOrder.orderType === 'CLAIM' && <Badge variant="order-claim"><>{productionOrder.orderType}</></Badge>}
              {productionOrder.orderType === 'FG' && <Badge variant="order-normal"><>{productionOrder.orderType}</></Badge>}
            </h6>
          </div>
          <div>
            <small>เข้าระบบโดย</small>
            <div className="flex items-center gap-1">
              <label className="text-xl font-bold ">{displayUsername}</label>
              <div className="relative ">
                <TbCopy className="cursor-pointer hover:bg-gray-200" size={20}
                  onClick={() => handleCopyToClipboard(displayUsername)}/>
                {copiedBatch === (displayUsername) && (
                  <div className="absolute z-50 p-2 mt-2 text-sm text-white transform -translate-y-full bg-black rounded -translate-x-1/3 -top-3">
                    Copied
                  </div>
                )}
              </div>
            </div>
          </div>
          <div>
            <small>Product Profiles</small>
            <div className='flex flex-wrap gap-1 mt-1'>
              {productionOrder?.infoFwt && <Badge variant='product-info'>{productionOrder?.infoFwt}</Badge>}
              {productionOrder?.infoEffBulkDate && <Badge variant='product-info'>{`Eff.Bulk : ${productionOrder?.infoEffBulkDate}`}</Badge>}
              {productionOrder?.infoRefPkg && <Badge variant='product-info'>{productionOrder?.infoRefPkg}</Badge>}
              {productionOrder?.infoPallet && <Badge variant='product-info'>{`Pallet: ${productionOrder?.infoPallet}`}</Badge>}
              {productionOrder?.infoPacking && <Badge variant='product-info'>{`Packing: ${productionOrder?.infoPacking}`}</Badge>}
            </div>
          </div>
          <div className="col-span-2 ">
            <small>BATCH LOT</small>
            <div className='flex flex-wrap gap-1 mt-1'>
              {batchNumbers.map(batch => (
                <div className="relative" key={batch}>
                  <AmplifyBadge
                    variation='warning'
                    className='amplify-badge-clickable'
                    onClick={() => handleCopyToClipboard(batch)}
                  >
                    {batch}
                    <TbCopy className="ml-2" />
                  </AmplifyBadge>
                  {copiedBatch === batch && (
                    <div className="absolute top-0 z-50 p-2 mt-2 text-sm text-white transform -translate-x-1/2 -translate-y-full bg-black rounded left-1/2">
                      Copied
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          {uiState === UIState.Loading && <div className="space-y-3 ">
            <SpinnerLoading  />
            <p className='text-center'>กำลังส่งงาน...</p>
          </div>}
          {uiState === UIState.Init && <Stepper
            steps={['เลือก FORM', 'บันทึกผ่าน Excel', 'ส่งงานเมื่อจบ']}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            disabledNext={disabledNext}
            complete={complete}
            onCompleted={onCompleted}
            draftOptions={{
              isDraft: true,
              draftStep: 2,
              onClickDraft: onClosed
            }}
            >
            <>
              {currentStep === 1 && (
                <div className='grid grid-cols-2 gap-3'>
                {fgInspectionLogs.map(form => (
                  <button title={form.inspectionType} key={form.inspectionType} onClick={() => {
                    setSelectedForm(form);
                    onNext();
                  }} disabled={!form.canEdit || readOnly}>
                    <FGInspectMenu
                      formLog={form}
                    />
                  </button>
                  
                ))}
                </div>
              )}
              {currentStep === 2 && selectedForm && (
                <div className="flex flex-col items-center gap-6">
                  <FGInspectMenu
                    formLog={selectedForm}
                    hideArrow={true}
                    showAction={selectedForm.id === '' || selectedForm.inspectionStatus === 'TESTING'}
                    onStartRecord={onLinkToExcelSharePoint}
                    loading={loading}
                  />
                  {selectedForm.inspectionStatus === 'SUBMITTED' && 
                  <Button
                    gap="0.3rem"
                    variation="primary"
                    className=" amplify-button-inspec"
                    onClick={onRework}>
                    <TbReload /> แก้ไข/เริ่มบันทึกใหม่
                  </Button>}
                </div>
              )}
              {currentStep === 3 && selectedForm && (
                <div className="flex flex-col items-center gap-6">
                  <FGInspectMenu
                    formLog={selectedForm}
                    hideArrow={true}
                  />
                  <CheckboxField
                    label="กดที่นี่เพื่อยืนยันว่าบันทึกครบถ้วนแล้ว ก่อนกดปุ่มยืนยันส่งงาน"
                    name="subscribe"
                    size="large"
                    value="yes"
                    checked={confirmChecked}
                    onChange={(e) => setConfirmChecked(e.target.checked)}
                  />
                </div>
              )}
            </>
          </Stepper>}
          {uiState === UIState.Success && <DoComplete title="ส่งงานสำเร็จ" subTitle="รอทุก FORM ส่งงานเสร็จ เพื่อจบขั้นตอนต่อไป" onClose={onClosed}/>}
        </div>
      </>
    </Modal>
  );
}

export default memo(FGInspection);
